import { useEffect } from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

// Section 2.4 is linked from Settings > "Help improve QLearn" (the session-
// recording switch), which opens /privacy-policy#session-recording in a new tab.
const SESSION_RECORDING_ID = "session-recording";
// 2.4 points back at that switch.
const RECORDING_SETTING_PATH = "/user-settings#help-improve-qlearn";
// Section 6.1 is linked from Settings → Delete account.
const ACCOUNT_DELETION_ID = "account-deletion";
const LINKED_SECTIONS = new Set([SESSION_RECORDING_ID, ACCOUNT_DELETION_ID]);
const RECORDING_PRIVACY_URL = "https://contentsquare.com/privacy-policy/";

const useStyles = makeStyles(() => ({
  root: {
    minHeight: "100vh",
    background: "var(--background-color)",
    color: "var(--text-color)",
    padding: "40px 24px",
    maxWidth: 860,
    margin: "0 auto",
  },
  section: {
    marginTop: 28,
  },
  subsection: {
    marginTop: 16,
  },
  highlight: {
    background: "var(--list-item-sel-color, #f0f4ff)",
    borderLeft: "4px solid #3f51b5",
    padding: "10px 16px",
    borderRadius: 4,
    margin: "12px 0",
  },
  contact: {
    marginTop: 8,
    lineHeight: 1.8,
  },
}));

export default function PrivacyPolicy() {
  const classes = useStyles();

  // The browser tries the fragment before this page has rendered, so scroll to
  // the linked section once it exists.
  useEffect(() => {
    const id = window.location.hash.replace(/^#/, "");
    if (!LINKED_SECTIONS.has(id)) return;
    const section = document.getElementById(id);
    if (section && typeof section.scrollIntoView === "function") section.scrollIntoView();
  }, []);

  return (
    <Box className={classes.root}>
      <Typography variant="h4" gutterBottom>
        Privacy Policy
      </Typography>
      <Typography variant="body2" className="subtitleStyle" style={{ color: "#808080" }}>
        Effective Date: April 19, 2026 &nbsp;|&nbsp; Last Updated: October 30, 2026
      </Typography>
      <Divider style={{ margin: "16px 0" }} />

      {/* 1 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>1. Introduction</Typography>
        <Typography variant="body2">
          Medlearnity ("we," "us," or "our") operates the QLearn platform accessible at
          https://www.medlearnity.com and through our mobile applications (collectively, the "Service").
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information
          when you use our Service.
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          By using the Service, you agree to the collection and use of information in accordance
          with this Privacy Policy.
        </Typography>
      </Box>

      {/* 2 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>2. Information We Collect</Typography>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>2.1 Personal Information</Typography>
          <Typography variant="body2">We may collect the following personal information when you register or use our Service:</Typography>
          <ul>
            <li><Typography variant="body2">Full name</Typography></li>
            <li><Typography variant="body2">Email address</Typography></li>
            <li><Typography variant="body2">Phone number (when provided for SMS verification)</Typography></li>
            <li><Typography variant="body2">Account credentials (username, password hash)</Typography></li>
            <li><Typography variant="body2">Profile information (specialty, exam type, study preferences)</Typography></li>
          </ul>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>2.2 Usage Data</Typography>
          <Typography variant="body2">We automatically collect information about how you interact with the Service, including:</Typography>
          <ul>
            <li><Typography variant="body2">Pages visited and features used</Typography></li>
            <li><Typography variant="body2">Study session duration and quiz performance</Typography></li>
            <li><Typography variant="body2">Device type, operating system, and browser</Typography></li>
            <li><Typography variant="body2">IP address and approximate location (country/state level)</Typography></li>
          </ul>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>2.3 Payment Information</Typography>
          <Typography variant="body2">
            Web payments are processed by Stripe, and in-app subscriptions on iOS
            are processed by Apple through In-App Purchase (with RevenueCat). We do
            not store full credit card numbers; for card payments we retain only
            the last four digits and transaction identifiers.
          </Typography>
        </Box>

        {/* Approved text (M3-PRIVACY-NOTICE-DRAFT.md, approved by Akshay
            2026-10-19: notice plus opt-out), verbatim apart from one
            narrowing on 2026-10-24: the App Store iPhone app never records
            (a home-screen shortcut to the website still does), and the
            2026-10-28 changes: the opt-out moved from the "Privacy choices"
            link to Settings > "Help improve QLearn" (which purges without a
            reload), Do Not Track is honoured like GPC, European and UK time
            zones default to off, and "reviewing" quizzes is named (the quiz
            review, /exam/<id>/<n>/review, is on the list). The page list is
            exactly what records (src/utils/session-replay.js
            REPLAY_ALLOWED_ROUTES). Not behind the Hotjar build flag; do not
            widen the wording without re-checking what records. */}
        <Box className={classes.subsection} data-policy-section={SESSION_RECORDING_ID}>
          <Typography variant="subtitle2" gutterBottom id={SESSION_RECORDING_ID}>
            2.4 Session Recording
          </Typography>
          <Typography variant="body2" paragraph>
            To understand where QLearn is confusing and improve it, we record how people use QLearn's
            pages: the daily ABSITE question, sign-in and sign-up, getting started, building, taking and
            reviewing quizzes, results, and upgrade screens. We use Hotjar (part of Contentsquare), a service
            provider, to do this. We do not record sessions in the QLearn iPhone app from the App Store.
          </Typography>
          <Typography variant="body2" paragraph>
            A recording shows what the page displayed and what you did on it: clicks, taps, scrolling,
            mouse movement and the answers you choose. It also includes the page address, the website
            that sent you, your device type, operating system, browser, screen size and country, and
            any technical errors on the page. Anything you type, including names, email addresses,
            phone numbers and verification codes, is hidden on your device before anything is sent.
            Your name and email address are also hidden where the page shows them. Your IP address is
            not kept for more than three days. We do not link recordings to your QLearn account.
          </Typography>
          <Typography variant="body2" paragraph>
            We use recordings only to improve the Service. Hotjar, which is part of Contentsquare, may
            also use this information to improve its own services. Recordings are deleted within 365
            days, and other usage data within 13 months.
          </Typography>
          <Typography variant="body2" paragraph>
            Hotjar and Contentsquare place cookies and similar storage in your browser, including one
            that recognizes your browser on later visits for up to 13 months.
          </Typography>
          <Typography variant="body2" paragraph>
            You can turn recording off at any time in <a href={RECORDING_SETTING_PATH}>Settings</a>,
            under "Help improve QLearn"; we then remember
            your choice in your browser. If your browser sends a Global Privacy Control or Do Not Track
            signal, we do not record. If your device is set to a European or UK time zone, recording is
            off unless you turn it on in Settings. Turning recording off removes Hotjar's and Contentsquare's
            cookies and stops any further recording. Contentsquare's privacy policy is at{" "}
            <a href={RECORDING_PRIVACY_URL} rel="noopener noreferrer" target="_blank">{RECORDING_PRIVACY_URL}</a>.
          </Typography>
        </Box>
      </Box>

      {/* 3 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>3. How We Use Your Information</Typography>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>3.1 Phone Numbers and SMS</Typography>
          <Typography variant="body2">When you provide your phone number, we use it <strong>solely</strong> for the following purposes:</Typography>
          <ul>
            <li><Typography variant="body2">Sending one-time passcodes (OTP) for account verification</Typography></li>
            <li><Typography variant="body2">Sending one-time passcodes for login authentication</Typography></li>
            <li><Typography variant="body2">Account security alerts at your request</Typography></li>
          </ul>
          <Typography variant="body2"><strong>We do not use your phone number for marketing, promotional, or advertising messages.</strong></Typography>
          <Box className={classes.highlight}>
            <Typography variant="body2">
              Your mobile opt-in data and phone number will not be shared with or sold to
              third parties or affiliates for marketing or promotional purposes.
            </Typography>
          </Box>
          <Typography variant="body2">
            SMS messages are sent only to users who have explicitly opted in via their account
            settings. You may opt out at any time by replying <strong>STOP</strong> to any SMS message from us,
            or by turning off SMS notifications in your account preferences.
          </Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>3.2 General Use of Personal Information</Typography>
          <Typography variant="body2">We use the information we collect to:</Typography>
          <ul>
            <li><Typography variant="body2">Create and manage your account</Typography></li>
            <li><Typography variant="body2">Provide, operate, and improve the Service</Typography></li>
            <li><Typography variant="body2">Authenticate your identity and secure your account</Typography></li>
            <li><Typography variant="body2">Respond to customer support requests</Typography></li>
            <li><Typography variant="body2">Send transactional emails (account confirmations, password resets)</Typography></li>
            <li><Typography variant="body2">Send practice-question emails to new accounts (see section 3.3)</Typography></li>
            <li><Typography variant="body2">Comply with legal obligations</Typography></li>
          </ul>
        </Box>

        {/* The new-account practice email (QLearn-service #790): at most 3
            (qlearn.practice-email.max-sends-per-account; "three" below must
            follow it), days 1, 3 and 6 after sign-up, on by default. Each
            email is labelled promotional; its "Unsubscribe or manage email
            preferences" page turns these off or, as a second choice, records
            an all-marketing opt-out on the account and in ActiveCampaign
            (contact created if missing). Settings > "Email Practice Question"
            is the same switch. Before each send the backend asks ActiveCampaign
            whether the address is unsubscribed there. Sent through SendGrid,
            covered by "email delivery" in section 4. Wording follows the email
            footer and the unsubscribe page; re-check both before changing it. */}
        <Box className={classes.subsection} data-policy-section="practice-email">
          <Typography variant="subtitle2" gutterBottom>3.3 Practice-Question Emails</Typography>
          <Typography variant="body2" paragraph>
            Practice-question emails are promotional practice reminders. They are on by default for new
            QLearn accounts, and we send at most three in your first week after signing up.
          </Typography>
          <Typography variant="body2" paragraph>
            You can turn them off in Settings, under "Email Practice Question", or with the "Unsubscribe or
            manage email preferences" link in each email. That link also lets you stop all marketing emails
            from QLearn and Medlearnity, which can take up to 10 business days to reach every mailing list.
            You will still receive account emails, such as receipts and sign-in codes.
          </Typography>
          <Typography variant="body2" paragraph>
            Before each practice-question email, we check your email address with ActiveCampaign, our
            email-marketing provider, so that we do not email you if you have unsubscribed there. If you stop
            all marketing emails, we record that choice on your QLearn account and in ActiveCampaign, adding
            your email address there if it is not already on file.
          </Typography>
        </Box>
      </Box>

      {/* 4 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>4. How We Share Your Information</Typography>
        <Typography variant="body2">We do not sell, trade, or rent your personal information to third parties.</Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>We may share your information only in the following limited circumstances:</Typography>
        <ul>
          <li><Typography variant="body2"><strong>Service providers:</strong> Trusted vendors who assist in operating our Service (e.g., cloud hosting, payment processing, email delivery, email marketing, analytics and session recording), bound by confidentiality agreements</Typography></li>
          <li><Typography variant="body2"><strong>Legal compliance:</strong> When required by law, court order, or governmental authority</Typography></li>
          <li><Typography variant="body2"><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, with prior notice to users</Typography></li>
          <li><Typography variant="body2"><strong>With your consent:</strong> For any other purpose with your explicit consent</Typography></li>
        </ul>
        <Box className={classes.highlight}>
          <Typography variant="body2">
            Mobile opt-in data (phone numbers and SMS consent) will never be shared with
            third parties for marketing or promotional purposes under any circumstances.
          </Typography>
        </Box>
      </Box>

      {/* 5 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>5. SMS Communications</Typography>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.1 Opt-In</Typography>
          <Typography variant="body2">
            You opt in to SMS communications by entering your phone number and enabling SMS
            verification in your QLearn account settings.
          </Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.2 Message Frequency</Typography>
          <Typography variant="body2">
            Message frequency depends on your account activity. OTP messages are sent only when
            you initiate a verification or login action. You will not receive unsolicited messages.
          </Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.3 Message and Data Rates</Typography>
          <Typography variant="body2">Standard message and data rates may apply depending on your mobile carrier plan.</Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.4 Opt-Out</Typography>
          <Typography variant="body2">To stop receiving SMS messages from QLearn:</Typography>
          <ul>
            <li><Typography variant="body2">Reply <strong>STOP</strong> to any SMS message from us</Typography></li>
            <li><Typography variant="body2">Or toggle off SMS in your account Preferences page</Typography></li>
          </ul>
          <Typography variant="body2">You will receive a final confirmation message upon opt-out.</Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.5 Help</Typography>
          <Typography variant="body2">For assistance, reply <strong>HELP</strong> to any SMS message or contact us at:</Typography>
          <ul>
            <li><Typography variant="body2">Email: support@medlearnity.com</Typography></li>
            <li><Typography variant="body2">Phone: +1 (631) 540-2499</Typography></li>
          </ul>
        </Box>
      </Box>

      {/* 6 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>6. Data Retention</Typography>
        <Typography variant="body2">
          We retain your personal information for as long as your account is active or as needed
          to provide the Service. Phone numbers associated with opted-out accounts are removed
          from our active SMS list within 24 hours of opt-out.
        </Typography>

        <Box className={classes.subsection} id={ACCOUNT_DELETION_ID}>
          <Typography variant="subtitle2" gutterBottom>6.1 Deleting Your Account</Typography>
          <Typography variant="body2" gutterBottom>
            <strong>How:</strong> on the QLearn website or in the QLearn iPhone app, tap Account to open your
            settings. Then choose Delete account, review what is deleted and what is kept, and confirm. A student
            account is deleted immediately. For a tutor or administrator account, Delete account sends a request
            to our team, which completes it within 7 days and emails the account address when it is done. You can
            also email support@medlearnity.com.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>What we delete:</strong> your account and profile (name, email address, phone number and
            sign-in methods), your quizzes, answers, scores, bookmarks, notes, comments, feedback and search
            history, your settings, tutor and mentorship links, referral code and referrals, your share links,
            and your sign-in sessions, which stop working at once. Deleting an invited friend's account cancels
            a referral reward that had not yet been given; rewards already given stay. If you sign in again
            later, you start with a new, empty account.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>What we keep, and why:</strong> records of payments, subscriptions, refunds and cash
            referral rewards, because tax and accounting law requires us to keep them. We take your name and account off
            them and use a random identifier instead; they still carry Stripe's or Apple's reference number for
            the payment. Stripe also keeps its own customer and invoice records. If you chose to stop all
            marketing emails, ActiveCampaign keeps your email address with that choice, so we do not email you
            again. For tutors
            and administrators, the record of which medical questions they reviewed or published is kept
            without their name. Statistics that never identified you, such as score percentiles, are not
            affected.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Subscriptions:</strong> deleting your account cancels a QLearn subscription paid on the web
            at once, voids its unpaid invoices and removes the card saved for it, unless that card also pays for
            another Medlearnity purchase. There is no automatic refund; contact support@medlearnity.com to ask
            about one. Deleting your account does not cancel a subscription bought in the App Store: Apple keeps
            billing it until you cancel it in your Apple ID settings. If you create a new
            account and use Restore Purchases, a subscription that is still billing moves to the new account.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Other companies we notify:</strong> we delete your Mixpanel (analytics) profile, with your
            name, email address and phone number, and ask Mixpanel and RevenueCat (in-app purchases) to delete
            any other data they hold about you; if you signed in with Apple, we revoke QLearn's access to your
            Apple ID; and we cancel your Stripe subscription as described above.
          </Typography>
          <Typography variant="body2">
            <strong>Backups:</strong> deleted data is removed from our live systems right away, except a few
            activity records that are removed within the hour and a deletion record that keeps your display name
            for up to 30 days while we finish the requests to other companies. Copies remain in our automated
            database backups for up to 8 days. We also keep a small number of manual database snapshots for
            disaster recovery, and deleted data can remain in them until those snapshots are retired. Application
            logs are kept for up to 30 days.
          </Typography>
        </Box>
      </Box>

      {/* 7 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>7. Data Security</Typography>
        <Typography variant="body2">We implement industry-standard security measures to protect your information, including:</Typography>
        <ul>
          <li><Typography variant="body2">Encryption in transit (TLS/HTTPS)</Typography></li>
          <li><Typography variant="body2">Encrypted storage for sensitive fields</Typography></li>
          <li><Typography variant="body2">OTP codes are hashed and never stored in plain text</Typography></li>
          <li><Typography variant="body2">Access controls and audit logging</Typography></li>
        </ul>
        <Typography variant="body2">
          No method of transmission over the internet is 100% secure. We cannot guarantee
          absolute security but are committed to protecting your data.
        </Typography>
      </Box>

      {/* 8 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>8. Children's Privacy</Typography>
        <Typography variant="body2">
          Our Service is not directed to individuals under the age of 18. We do not knowingly
          collect personal information from children under 18. If we become aware that a child
          has provided us with personal information, we will delete it promptly.
        </Typography>
      </Box>

      {/* 9 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>9. Your Rights</Typography>
        <Typography variant="body2">Depending on your jurisdiction, you may have the right to:</Typography>
        <ul>
          <li><Typography variant="body2">Access the personal information we hold about you</Typography></li>
          <li><Typography variant="body2">Request correction of inaccurate data</Typography></li>
          <li><Typography variant="body2">Request deletion of your data</Typography></li>
          <li><Typography variant="body2">Withdraw consent at any time (including SMS opt-out)</Typography></li>
          <li><Typography variant="body2">Lodge a complaint with a data protection authority</Typography></li>
        </ul>
        <Typography variant="body2">
          To delete your account, use Delete account in Settings (the Account tab in the iPhone app); see
          section 6.1. To exercise the other rights, contact us at support@medlearnity.com.
        </Typography>
      </Box>

      {/* 10 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>10. Changes to This Privacy Policy</Typography>
        <Typography variant="body2">
          We may update this Privacy Policy from time to time. We will notify you of material
          changes by posting the new policy on this page and updating the "Last Updated" date.
          Continued use of the Service after changes constitutes acceptance of the updated policy.
        </Typography>
      </Box>

      {/* 11 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>11. Contact Us</Typography>
        <Typography variant="body2" className={classes.contact}>
          <strong>Medlearnity</strong><br />
          15608 Riverside Dr W, Apt 5F<br />
          New York, NY 10032<br />
          United States<br />
          Email: support@medlearnity.com<br />
          Phone: +1 (631) 540-2499<br />
          Website: https://www.medlearnity.com
        </Typography>
      </Box>

      <Box style={{ marginTop: 40, marginBottom: 16 }}>
        <Divider />
      </Box>
    </Box>
  );
}
